// The voices you can pick for voice mode: ten Deepgram Aura-2 voices across gender, age and
// accent. The choice is remembered per assistant (Morrow, or each agent) on this device.
import { useCallback, useState } from "react";

export interface VoiceOption {
  /** Aura-2 speaker id (allow-listed by the API worker). */
  readonly id: string;
  readonly name: string;
  /** Who it sounds like, in a few words. */
  readonly description: string;
}

export const VOICE_OPTIONS: ReadonlyArray<VoiceOption> = [
  { id: "luna", name: "Luna", description: "Young woman · American · friendly" },
  { id: "orion", name: "Orion", description: "Man · American · calm, easygoing" },
  { id: "athena", name: "Athena", description: "Older woman · American · calm, professional" },
  { id: "atlas", name: "Atlas", description: "Older man · American · warm, upbeat" },
  { id: "janus", name: "Janus", description: "Woman · Southern US · smooth, trustworthy" },
  { id: "zeus", name: "Zeus", description: "Man · American · deep, steady" },
  { id: "pandora", name: "Pandora", description: "Woman · British · smooth, melodic" },
  { id: "draco", name: "Draco", description: "Man · British · warm baritone" },
  { id: "theia", name: "Theia", description: "Woman · Australian · sincere" },
  { id: "hyperion", name: "Hyperion", description: "Man · Australian · caring" },
  { id: "amalthea", name: "Amalthea", description: "Young woman · Filipino · cheerful" },
];

export function voiceOption(id: string): VoiceOption | undefined {
  return VOICE_OPTIONS.find((v) => v.id === id);
}

const storageKey = (who: string) => `morrow:voice:${who}`;

/** The voice picked for `who` (falls back to `fallback`), and a setter that remembers it. */
export function useVoiceChoice(who: string, fallback: string): [string, (id: string) => void] {
  const read = () => {
    try {
      const saved = localStorage.getItem(storageKey(who));
      return saved && voiceOption(saved) ? saved : fallback;
    } catch {
      return fallback;
    }
  };
  const [state, setState] = useState<{ who: string; id: string }>(() => ({ who, id: read() }));
  // A different assistant (e.g. navigating between agents) reads its own choice.
  const current = state.who === who ? state.id : read();
  const choose = useCallback(
    (id: string) => {
      setState({ who, id });
      try {
        localStorage.setItem(storageKey(who), id);
      } catch {
        /* private mode: still applies for this session */
      }
    },
    [who],
  );
  return [current, choose];
}
